<template>
  <div class="news-menu">
    <!-- Show loading placeholders if categories are loading -->
    <div v-if="isLoading" class="news-menu-loading">
      <div v-for="n in 6" :key="n" class="news-menu-item-placeholder">
        <span class="placeholder-text">{{ $t('common.loading') }}</span>
      </div>
    </div>
    <!-- Show actual menu items when loaded -->
    <div v-else-if="categories.length > 0" class="news-menu-loaded">
      <news-menu-item
        v-for="category in categories"
        :key="category.id"
        :title="localize(category.name)"
        :is-active="isCategoryActive(category.id)"
        :link="getCategoryLink(category.id)"
      />
    </div>
  </div>
</template>
<style scoped>
.news-menu {
  display: flex;
  background-color: black;
  padding: 10px 0;
  min-height: 44px; /* Prevent jumping by maintaining consistent height */
}

.news-menu-loading,
.news-menu-loaded {
  display: flex;
  width: 100%;
}

.news-menu-item-placeholder {
  margin: 0 12px;
  padding: 8px 16px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
}

.placeholder-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;

}

@media (max-width: 768px) {
  .news-menu {
    padding: 8px 0;
    min-height: 40px;
  }

  .news-menu-loading,
  .news-menu-loaded {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 6px;
    padding: 0 8px;
    box-sizing: border-box;
  }

  .news-menu-item-placeholder {
    margin: 0 6px;
    padding: 6px 12px;
  }

  .placeholder-text {
    font-size: 13px;
  }
}
</style>
<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import NewsMenuItem from './news-menu-item.vue';
import { Language, getNewsSourceLanguage, getNewsTargetLanguage } from '@lingvo-news/contracts';
import { useCategoryStore } from '../../../store/categories/category-store';
import { useCurrentPreferences } from '../../../composables/useCurrentPreferences';
import { localize } from '../../../utils/localize';

interface Props {
  currentCategoryId?: string;
}

const props = defineProps<Props>();

const router = useRouter();
const categoryStore = useCategoryStore();
const { nativeLanguage, learnedLanguage, mode } = useCurrentPreferences();

const newsSourceLanguage = computed(() => {
  if (!nativeLanguage.value || !learnedLanguage.value) return null;
  return getNewsSourceLanguage({
    nativeLanguage: nativeLanguage.value,
    learnedLanguage: learnedLanguage.value,
    mode: mode.value
  });
});

const newsTargetLanguage = computed(() => {
  if (!nativeLanguage.value || !learnedLanguage.value) return null;
  return getNewsTargetLanguage({
    nativeLanguage: nativeLanguage.value,
    learnedLanguage: learnedLanguage.value,
    mode: mode.value
  });
});

const loader = computed(() => categoryStore.getLoader(
  newsSourceLanguage.value ?? Language.EN,
  newsTargetLanguage.value ?? Language.EN,
  mode.value
));

const categories = computed(() => loader.value.categories);
const isLoading = computed(() => loader.value.isLoading());

watch([nativeLanguage, learnedLanguage, mode], async () => {
  await loader.value.loadCategories();
}, { immediate: true });

const isCategoryActive = (categoryId: string) => {
  return props.currentCategoryId === categoryId;
};

const getCategoryLink = (categoryId: string) => {
  return {
    name: "news-list",
    params: {
      nativeLanguage: nativeLanguage.value!,
      learnedLanguage: learnedLanguage.value!,
      mode: mode.value,
      categoryId: categoryId,
    },
  };
};
</script>
